// Messages from the same person within this window are shown as one block
// (avatar and name only on the first one), like Rocket.Chat and Slack.
const GROUP_WINDOW_SECONDS = 5 * 60;

const CHANNEL_COLORS = [
  'bg-n-blue-3 text-n-blue-11',
  'bg-n-teal-3 text-n-teal-11',
  'bg-n-iris-3 text-n-iris-11',
  'bg-n-amber-3 text-n-amber-11',
  'bg-n-ruby-3 text-n-ruby-11',
  'bg-n-violet-3 text-n-violet-11',
];

const dayKey = unix => {
  const date = new Date(unix * 1000);
  return `${date.getFullYear()}-${date.getMonth()}-${date.getDate()}`;
};

const authorOf = message => message.user_id ?? message.user?.id;

export const buildTimeline = messages => {
  const items = [];
  let previous = null;
  messages.forEach(message => {
    const sameDay =
      previous && dayKey(previous.created_at) === dayKey(message.created_at);
    if (!sameDay) {
      items.push({
        type: 'day',
        key: `day-${dayKey(message.created_at)}`,
        unix: message.created_at,
      });
    }
    const continued =
      sameDay &&
      authorOf(previous) === authorOf(message) &&
      message.created_at - previous.created_at < GROUP_WINDOW_SECONDS;
    items.push({ type: 'message', key: message.id, message, continued });
    previous = message;
  });
  return items;
};

// vue-i18n uses "pt_BR"; Intl only accepts BCP 47 ("pt-BR") and throws otherwise.
const intlLocale = locale => locale.replace('_', '-');

export const dayLabel = (unix, t, locale) => {
  const date = new Date(unix * 1000);
  const today = new Date();
  const yesterday = new Date();
  yesterday.setDate(today.getDate() - 1);
  if (dayKey(unix) === dayKey(today.getTime() / 1000)) {
    return t('TEAM_CHAT.TODAY');
  }
  if (dayKey(unix) === dayKey(yesterday.getTime() / 1000)) {
    return t('TEAM_CHAT.YESTERDAY');
  }
  return date.toLocaleDateString(intlLocale(locale), {
    weekday: 'long',
    day: 'numeric',
    month: 'long',
    ...(date.getFullYear() === today.getFullYear() ? {} : { year: 'numeric' }),
  });
};

export const formatTime = (unix, locale) =>
  new Date(unix * 1000).toLocaleTimeString(intlLocale(locale), {
    hour: '2-digit',
    minute: '2-digit',
  });

export const channelColor = channel => {
  const name = channel?.name || '';
  const hash = [...name].reduce((sum, char) => sum + char.charCodeAt(0), 0);
  return CHANNEL_COLORS[hash % CHANNEL_COLORS.length];
};

export const channelDisplayName = channel =>
  channel.private ? channel.name : `#${channel.name}`;

// One-line text for a quote: mentions as "@Name", no markdown marks.
export const quoteText = content =>
  (content || '')
    .replace(/\[([^\]]+)\]\(mention:\/\/[^)]+\)/g, '@$1')
    .replace(/[*_`~#>]/g, '')
    .replace(/\s+/g, ' ')
    .trim();

export const senderName = message =>
  message.user?.available_name || message.user?.name || '';
